import { useCallback, useEffect, useRef } from 'react';
import { useFireTrackingEvent } from '../../../hooks/tracking/useTrackingEvents';
import { Mode } from '.';

const useTrackScroll = (
  mode: Mode,
  trackingId: string,
  container: React.RefObject<HTMLDivElement>,
  tracked: React.RefObject<string[]>,
  image: React.MutableRefObject<string>,
) => {
  const track = useFireTrackingEvent();

  useEffect(() => {
    if (!container.current || mode !== 'scroll') {
      return;
    }

    const handleObserve = (entries: IntersectionObserverEntry[]) => {
      entries
        .filter((e) => e.isIntersecting)
        .map((el) => el.target.attributes.getNamedItem('data-img')?.value)
        .forEach((id) => {
          if (!id) {
            return;
          }

          image.current = id;

          if (!tracked.current?.includes(id)) {
            tracked.current?.push(id);

            track({
              event: 'multi_image_hero_image_view',
              category: trackingId,
              value: id,
            });
          }
        });
    };

    const observer = new IntersectionObserver(handleObserve, {
      root: container.current,
      threshold: 0.5,
    });

    container.current.querySelectorAll('[data-img]').forEach((el: Element) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, []);
};

const useTrackFade = (
  mode: Mode,
  trackingId: string,
  container: React.RefObject<HTMLDivElement>,
  tracked: React.RefObject<string[]>,
  _: React.MutableRefObject<string>,
  index: number,
) => {
  const nodes = useRef<NodeListOf<HTMLElement>>();
  const track = useFireTrackingEvent();

  useEffect(() => {
    if (!container.current || mode !== 'fade') {
      return;
    }

    nodes.current = container.current.querySelectorAll('[data-img]');
  }, []);

  useEffect(() => {
    if (mode !== 'fade' || !nodes.current?.length) {
      return;
    }

    const id = nodes.current[index].attributes.getNamedItem('data-img')?.value;

    if (id && !tracked.current?.includes(id)) {
      tracked.current?.push(id);

      track({
        event: 'multi_image_hero_image_view',
        category: trackingId,
        value: id,
      });
    }
  }, [index]);
};

export const useTrackHero = (
  mode: Mode,
  trackingId: string,
  container: React.RefObject<HTMLDivElement>,
  index: number,
  defaultImage?: string,
) => {
  const tracked = useRef<string[]>([]);
  const image = useRef<string>(defaultImage || '');
  const track = useFireTrackingEvent();
  useTrackScroll(mode, trackingId, container, tracked, image);
  useTrackFade(mode, trackingId, container, tracked, image, index);

  return useCallback((text: string) => {
    track({
      event: 'multi_image_hero_click',
      category: trackingId,
      label: image.current,
      value: text,
    });
  }, []);
};
